import React, { memo, useCallback, useMemo, useState } from 'react';
import { Button } from 'components/ui/button';
import { Paper } from '@app/components';
import { Company, User, UserType } from '@app/models';
import { getCompanyByBin, getUserByIin } from '@app/api';
import { fullName } from '@app/helpers';
import { useUserContext } from '@app/providers';

interface Props {
  onSubmit: (company: Company | null, user: User | null) => void;
}

function Step1(props: Props) {
  const { onSubmit } = props;
  const { user: authUser, company: authCompany } = useUserContext();
  const [pending, setPending] = useState<boolean>(false);
  const [bin, setBin] = useState<string>('');
  const [company, setCompany] = useState<Company | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [searchError, setSearchError] = useState<boolean>(false);

  const onChangeBin = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCompany(null);
    setUser(null);
    setBin(e.target.value);
  }, []);

  const onClickFind = useCallback(async () => {
    try {
      setPending(true);
      setSearchError(false);
      const companyResponse = await getCompanyByBin(bin);
      const userResponse = await getUserByIin(bin);
      let isCurrentCompany = false;
      let isCurrentUser = false;
      if (
        !!companyResponse &&
        authUser.type === UserType.ENTITY &&
        companyResponse.id === authCompany!.id
      ) {
        isCurrentCompany = true;
      }
      if (
        !!userResponse &&
        userResponse.type === UserType.INDIVIDUAL &&
        userResponse.id === authUser!.id
      ) {
        isCurrentUser = true;
      }
      setCompany(!isCurrentCompany ? companyResponse : null);
      setUser(!isCurrentUser ? userResponse : null);
      setPending(false);
      setSearchError(
        (!companyResponse && !userResponse) || isCurrentCompany || isCurrentUser
      );
    } catch (e) {
      setPending(false);
      setSearchError(false);
    }
  }, [authCompany, authUser, bin]);

  const onClickSubmit = useCallback(() => {
    onSubmit(company, user);
  }, [company, user, onSubmit]);

  const partnerName = useMemo<string>(() => {
    let returnName = '';
    if (!!company) {
      returnName = company.name;
    }
    if (!!user) {
      returnName = fullName(user);
    }
    return returnName;
  }, [company, user]);

  return (
    <div className="bg-card rounded-lg p-6 shadow w-full max-w-2xl mx-auto flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Партнер</label>
            <input
              className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-base placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={partnerName}
              disabled
            />
          </div>
          <div>
            <label htmlFor="bin" className="block text-sm font-medium mb-1">
              БИН/ИИН
            </label>
            <input
              id="bin"
              className={`w-full h-10 rounded-md border px-3 py-2 text-base placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                searchError ? 'border-red-500' : 'border-input'
              }`}
              placeholder="Укажите БИН/ИИН"
              value={bin}
              onChange={onChangeBin}
              maxLength={12}
            />
            {searchError && (
              <div className="text-xs text-red-500 mt-1">
                Вы не можете добавить себя в качестве партнера
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex gap-4 justify-end">
        <Button onClick={onClickFind} disabled={pending || bin.length !== 12}>
          {pending ? 'Поиск...' : 'Найти'}
        </Button>
        {(!!company || !!user) && (
          <Button onClick={onClickSubmit}>Продолжить</Button>
        )}
      </div>
    </div>
  );
}

export default memo(Step1);
